<script lang="ts">
    import { onMount } from "svelte";

    import SVGIcon from "$/components/SVGIcon.svelte";

    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import {
        checkAndGenerateSocialMediaURL,
        toggleSocialMediaPostSaveState,
    } from "$/utils/Utils";
    import { AppStore } from "$/stores/AppStore";
    import { v4 as uuid } from "uuid";
    import {ExtensionStore} from "$/stores/ExtensionStore";

    let baseSaveButton: HTMLButtonElement;
    let mutationObserver: MutationObserver;
    let isInitialLoad = false;
    export let editRole: number;

    // Define the classes of Quora content bundles that we are interested in.
    let quoraContentBundles = [
        // Ad's and Answer bundles are not needed since theres no content to save
        // "dom_annotate_multifeed_bundle_AdBundle",
        // "dom_annotate_multifeed_bundle_AskQuestionPromptBundle",
        // "dom_annotate_multifeed_bundle_QuestionsBundle",
        // "dom_annotate_multifeed_bundle_TribeSuggestionsBundle"

        // TODO: "Could not get element with this bundle to appear on quora atm"
        "dom_annotate_multifeed_bundle_ExploreAnswerBundle",

        "dom_annotate_multifeed_bundle_AnswersBundle",
        "dom_annotate_multifeed_bundle_PostBundle",
        // HINT: dom_annotate_multifeed_bundle_TribeContentBundle is for not logged in users
        "dom_annotate_multifeed_bundle_TribeContentBundle",
    ];

    const isPostWithAllRelated = (currentUrl) => {
        if (isPostPageURL(currentUrl)) {
            // get all elements with q-text
            // check if any of the elements contains "All related (100+)"
            let allRelated = document.querySelectorAll(".q-text");
            let allRelatedText = Array.from(allRelated).map(
                (el) => el.textContent,
            );

            return allRelatedText.some((text) => text.includes("All related"));
        }
    };

    const isPostPageURL = (currentUrl) => {
        let url = new URL(currentUrl);
        // Check is there some string after .com/
        let isPost = url.pathname.split("/")[1] !== "";
        let ogType = document
            .querySelector('meta[property="og:type"]')
            ?.getAttribute("content");
        // Check if og:type is "article" to make sure we are on a post page
        return isPost && ogType === "article";
    };

    const isProfilePage = (currentUrl) => {
        let url = new URL(currentUrl);
        return (
            url.pathname.split("/")[1] === "profile" &&
            url.hostname === "www.quora.com"
        );
    };

    const isSearchPage = (currentUrl) => {
        let url = new URL(currentUrl);
        return (
            url.pathname.split("/")[1] === "search" &&
            url.hostname === "www.quora.com"
        );
    };

    const createPostSaveButton = (post) => {
        const postSaveButton = baseSaveButton.cloneNode(
            true,
        ) as HTMLButtonElement;

        postSaveButton.classList.remove("cw-save-hide");
        postSaveButton.addEventListener("click", async () => {
            event.preventDefault();
            event.stopPropagation();

            toggleSocialMediaPostSaveState(postSaveButton, true);

            let data = JSON.parse(postSaveButton.dataset.postData);
            // Clean the content from html tags and remove new lines add spaces instead,
            // Remove multiple spaces with one and cut if its longer than 100 characters
            let cleanTitleSuggestion = data.postContent?.replace(
                /<[^>]*>?/gm,
                "",
            );
            cleanTitleSuggestion = cleanTitleSuggestion?.replace(/\n/g, " ");
            cleanTitleSuggestion = cleanTitleSuggestion?.replace(/\s\s+/g, " ");
            let titleSuggestion =
                cleanTitleSuggestion?.length > 100
                    ? cleanTitleSuggestion.slice(0, 100) + "..."
                    : cleanTitleSuggestion;

            if (data.postContent.length > 10000)
                data.postContent =
                    data.postContent.substring(0, 10000 - 3) + "...";

            let snippet = {
                id: uuid(),
                content: data.postContent ?? "No content",
                nameSuggestion: titleSuggestion,
                comments: [],
                url: checkAndGenerateSocialMediaURL("quora", data.url),
                createdBy: $TokenStore?.user,
                createdOn: new Date().getTime(),
            };

            ExtensionStore.setOpened(true);

            ActiveTopicStore.createSnippet(snippet);

            // set focus on the newly created snippet
            AppStore.setFocused({ snippetId: snippet.id });

            // hide the loading spinner
            toggleSocialMediaPostSaveState(postSaveButton, false);
        });

        postSaveButton.dataset.postData = JSON.stringify(post);

        return postSaveButton;
    };

    const extractDataFromPost = (element: HTMLElement) => {
        return {
            url: extractPostUrl(element),
            postContent: extractPostContent(element),
        };
    };

    const addSaveButtonsToAllPosts = () => {
        // Find all elements with quoraContentBundles classes or if we are on post page, get mainContent element
        let allPosts: NodeListOf<HTMLElement>[] | [];
        if (
            isPostPageURL(window.location.href) &&
            !isProfilePage(window.location.href)
        ) {
            let mainContent = [
                document.getElementById("mainContent") as HTMLElement,
            ];

            if (
                isPostWithAllRelated(window.location.href) &&
                mainContent.length > 0
            ) {
                // from main content get all elements with class "gCXnis"
                let allRelated = Array.from(
                    mainContent[0].querySelectorAll(".gCXnis"),
                ) as HTMLElement[];
                let allRelatedParents = allRelated.map(
                    (el) =>
                        el?.parentElement?.parentElement?.parentElement
                            ?.parentElement as HTMLElement,
                );
                allPosts = [allRelatedParents] as unknown as
                    | NodeListOf<HTMLElement>[]
                    | [];
            } else
                allPosts = [mainContent] as unknown as
                    | NodeListOf<HTMLElement>[]
                    | [];
        } else if (isSearchPage(window.location.href)) {
            let searchPosts = document.querySelectorAll(
                ".qu-zIndex--action_bar",
            );
            let puppeteerPosts = document.querySelectorAll(
                ".puppeteer_test_question_component_base",
            );
            let searchPostParents = Array.from(searchPosts).map(
                (post) =>
                    post?.parentElement?.parentElement?.parentElement
                        ?.parentElement as HTMLElement,
            );
            allPosts = [searchPostParents, puppeteerPosts] as unknown as
                | NodeListOf<HTMLElement>[]
                | [];
        } else if (
            isProfilePage(window.location.href) &&
            !isPostPageURL(window.location.href)
        ) {
            let profilePosts = document.querySelectorAll(
                ".spacing_log_answer_content",
            );
            let profilePostsParents = Array.from(profilePosts).map(
                (post) => post.parentElement?.parentElement as HTMLElement,
            );
            allPosts = [profilePostsParents] as unknown as
                | NodeListOf<HTMLElement>[]
                | [];
        } else if (
            !isPostPageURL(window.location.href) &&
            !isProfilePage(window.location.href)
        ) {
            allPosts = quoraContentBundles.map((bundle) =>
                document.querySelectorAll(`.${bundle}`),
            ) as NodeListOf<HTMLElement>[] | [];
        }
        // Correctly handle nested NodeListOf<Element> structures, we cant use .flat() since TS doesn't recognize it
        allPosts.forEach((posts: []) => {
            posts.forEach((post: HTMLElement) => {
                if (!post.querySelector(".cw-save-post-btn")) {
                    const postSaveButton = createPostSaveButton(
                        extractDataFromPost(post),
                    );

                    const socialActions = post.querySelector(
                        ".qu-zIndex--action_bar",
                    )?.firstElementChild?.firstElementChild?.firstElementChild
                        ?.firstElementChild;
                    socialActions?.appendChild(postSaveButton);
                }
            });
        });
    };

    const removeUnwantedElementsFromPost = (qBoxElement: HTMLElement) => {
        if (qBoxElement && qBoxElement.children.length > 0)
            qBoxElement.removeChild(qBoxElement.children[0]);

        let imgElements = qBoxElement?.querySelectorAll("img");
        if (imgElements) imgElements.forEach((img) => img.remove());

        let readMoreElement = qBoxElement?.querySelector(".qt_read_more");
        if (readMoreElement) readMoreElement.remove();

        return qBoxElement;
    };

    const extractPostUrl = (element: HTMLElement) => {
        let postTimestampUrl = "";

        const postTimestampElement = element.querySelector(".post_timestamp");
        if (postTimestampElement) {
            postTimestampUrl = postTimestampElement?.getAttribute("href") ?? "";
        } else {
            if (isSearchPage(window.location.href))
                postTimestampUrl =
                    element
                        ?.querySelector(".answer_timestamp")
                        ?.getAttribute("href") ?? "";
            else {
                const answerTimestampElement = element.querySelector(
                    ".answer_timestamp",
                )
                    ? element.querySelector(".answer_timestamp")
                    : element.querySelector(".gOCLNQ");
                if (answerTimestampElement)
                    postTimestampUrl =
                        answerTimestampElement?.getAttribute("href") ?? "";
                else {
                    // try to find a element
                    const postUrlElement = element.querySelector("a");
                    if (postUrlElement)
                        postTimestampUrl =
                            postUrlElement?.getAttribute("href") ?? "";
                }
            }
        }
        return postTimestampUrl ?? "quora.com";
    };

    const extractPostContent = (element: HTMLElement) => {
        let elementClasses = element?.classList;
        let content = "";

        if (isSearchPage(window.location.href)) {
            return element.querySelector(".puppeteer_test_answer_content")
                ?.textContent.length
                ? element.querySelector(".puppeteer_test_answer_content")
                      .textContent
                : "No content";
        } else if (
            isPostPageURL(window.location.href) &&
            !isProfilePage(window.location.href)
        ) {
            // Check if www is in the url, if it is, we are on a post page with iyYUZT else we are on a post page without iyYUZT
            // Which is crucial for getting the correct qBoxElement
            let currentUrl = new URL(window.location.href);
            let isWWW = currentUrl.hostname.includes("www");

            const qBoxElementQuora = isWWW
                ? element?.firstElementChild?.firstElementChild
                      ?.firstElementChild
                : element?.querySelector(".qu-zIndex--action_bar").parentElement
                      ?.firstElementChild;

            const qBoxElement = qBoxElementQuora?.cloneNode(
                true,
            ) as HTMLElement;

            if (isPostWithAllRelated(window.location.href)) {
                let qBoxElement =
                    element.firstElementChild?.firstElementChild.cloneNode(
                        true,
                    ) as HTMLElement;
                const cleanQBoxElement = removeUnwantedElementsFromPost(
                    qBoxElement as HTMLElement,
                );
                content = cleanQBoxElement?.textContent.trim()
                    ? cleanQBoxElement.innerHTML
                    : "No content";
                return content;
            }

            // If we are on a post page without iyYUZT, we need to swap the first and second child for our qBoxElement to be correct
            else if (isWWW) {
                let firstChild = qBoxElement.children[0];
                let secondChild = qBoxElement.children[1];

                // Swap without directly removing them
                qBoxElement.insertBefore(secondChild, firstChild);
                qBoxElement.insertBefore(firstChild, secondChild.nextSibling);

                // Remove the last child since its a views count and etc...
                qBoxElement.removeChild(qBoxElement.lastElementChild);
            }

            const cleanQBoxElement =
                removeUnwantedElementsFromPost(qBoxElement);
            content = cleanQBoxElement?.textContent.trim()
                ? cleanQBoxElement.innerHTML
                : "No content";
            return content;
        } else if (
            isProfilePage(window.location.href) &&
            !isPostPageURL(window.location.href)
        ) {
            const contentElement = element.querySelector(
                ".spacing_log_answer_content",
            )?.parentElement;
            const qBoxElement = contentElement?.cloneNode(true) as HTMLElement;
            const cleanQBoxElement =
                removeUnwantedElementsFromPost(qBoxElement);
            content = cleanQBoxElement?.textContent.trim()
                ? cleanQBoxElement.innerHTML
                : "No content";
            return content;
        }

        for (let bundle of quoraContentBundles) {
            if (elementClasses.contains(bundle)) {
                // For each bundle, get the content element and extract the content for its respective bundle
                if (bundle === "dom_annotate_multifeed_bundle_PostBundle") {
                    const contentElement = element.querySelector(
                        ".puppeteer_test_tribe_post_item_feed_story",
                    )?.children[1];

                    const qBoxElementQuora = contentElement?.querySelector(
                        ".qu-zIndex--action_bar",
                    ).parentElement?.firstElementChild;
                    const qBoxElement = qBoxElementQuora?.cloneNode(
                        true,
                    ) as HTMLElement;
                    const cleanQBoxElement =
                        removeUnwantedElementsFromPost(qBoxElement);

                    content = cleanQBoxElement?.textContent.trim()
                        ? cleanQBoxElement.innerHTML
                        : "No content";
                }

                if (bundle === "dom_annotate_multifeed_bundle_AnswersBundle") {
                    const contentElement = element.querySelector(
                        ".puppeteer_test_answer_content",
                    )?.parentElement;

                    const qBoxElement = contentElement?.cloneNode(
                        true,
                    ) as HTMLElement;
                    const cleanQBoxElement =
                        removeUnwantedElementsFromPost(qBoxElement);

                    content = cleanQBoxElement?.textContent.trim()
                        ? cleanQBoxElement.innerHTML
                        : "No content";
                }

                if (
                    bundle ===
                    "dom_annotate_multifeed_bundle_TribeContentBundle"
                ) {
                    const contentElement =
                        element.querySelector(
                            ".puppeteer_test_tribe_post_item_feed_story",
                        ) ||
                        element.querySelector(
                            ".puppeteer_test_tribe_answer_feed_story",
                        );

                    const qBoxElementQuora = contentElement?.classList.contains(
                        "puppeteer_test_tribe_post_item_feed_story",
                    )
                        ? contentElement?.children[1]?.firstElementChild
                              ?.firstElementChild?.firstElementChild
                              ?.firstElementChild
                        : contentElement?.firstElementChild?.firstElementChild
                              ?.firstElementChild?.firstElementChild
                              ?.firstElementChild;
                    const qBoxElement = qBoxElementQuora?.cloneNode(
                        true,
                    ) as HTMLElement;
                    const cleanQBoxElement =
                        removeUnwantedElementsFromPost(qBoxElement);

                    content = cleanQBoxElement?.textContent.trim()
                        ? cleanQBoxElement.innerHTML
                        : "No content";
                }
                return content;
            }
        }

        return "No content";
    };

    const checkURLAndAddSaveButtons = () => {
        const currentUrl = window.location.href;
        if (currentUrl.includes("quora.com")) {
            let bundleName =
                quoraContentBundles.find((bundle) =>
                    document.querySelector(`.${bundle}`),
                ) ?? "";
            if (
                bundleName === "" ||
                isPostPageURL(currentUrl) ||
                isProfilePage(currentUrl)
            ) {
                setTimeout(() => {
                    addSaveButtonsToAllPosts();
                }, 1000);
            } else {
                addSaveButtonsToAllPosts();
            }
        }

        mutationObserver.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    };

    const removeAllSaveButtons = () => {
        const saveButtons = document.querySelectorAll(".cw-save-post-btn");
        saveButtons.forEach((button: HTMLElement) => button.remove());

        mutationObserver?.disconnect();
    };

    onMount(() => {
        mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === "childList" &&
                    window.location.href.includes("quora.com") &&
                    editRole >= 3
                ) {
                    mutation.addedNodes.forEach(() => {
                        addSaveButtonsToAllPosts();
                    });
                }
            });
        });

        if (editRole >= 3) {
            checkURLAndAddSaveButtons();
        }

        isInitialLoad = true;
    });

    $: isInitialLoad && editRole >= 3
        ? checkURLAndAddSaveButtons()
        : removeAllSaveButtons();
</script>

{#if window.location.href.includes("quora.com")}
    <button
        class="cw-save-hide cw-save-post-btn cw-quora-btn"
        title="Save as Snippet"
        bind:this={baseSaveButton}
    >
        <div class="cw-tw-bg cw-quora-bg"></div>
        <div class="cw-save__loading-spinner quora" style="display: none;">
            <SVGIcon
                name="spinner"
                color="#666666"
                size="20"
                viewbox="0 0 24 24"
                strokeWidth="2"
            />
        </div>
        <div class="cw-save__icon">
            <SVGIcon
                name="save"
                size="20"
                color="#666666"
                viewbox="0 0 28 28"
                strokeWidth="0.2"
            />
        </div>
    </button>
{/if}

<style>
    @import "../../extension/external.css";
</style>
